import {
  Button,
  Drawer,
  Empty,
  Form,
  Pagination,
  Popover,
  Table,
  Tabs,
  Tag,
  message,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Router from "../..";
import { useUserStore } from "../../../stores/userStore";
import { AGENCY_STATUS } from "../../utils/campaignStatus";
import {
  useAppliedInfluencersByAgency,
  useCampaignListByAgency,
  useCancelCampaignByAgency,
} from "../../hooks/useCampaign";
import { useAgencyPaymentHistory } from "../../hooks/usePayment";
import { formatCurrency, parseApiAmount } from "../../utils/amountFormat";
import {
  influencerPaymentStatusClass,
  influencerPaymentStatusLabel,
} from "../../utils/influencerPaymentStatus";

export const AgencyCampaignManagement = () => {
  const { userInfo } = useUserStore();
  const history = useHistory();
  const [isDetailDrawerVisible, setIsDetailDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  const location = useLocation();

  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAppliedList, setShowAppliedList] = useState(false);
  const [influencerListStatus, setInfluencerListStatus] = useState(null);
  const pageSize = 20;

  // 체크박스 선택 상태
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const { data: appliedData } = useAppliedInfluencersByAgency({
    campaignId: selectedCampaign?.id,
    status: influencerListStatus,
    enabled: !!selectedCampaign?.id,
  });

  const { mutateAsync: cancelCampaignByAgency } = useCancelCampaignByAgency();

  const handleCancelCampaign = async () => {
    if (selectedRowKeys.length === 0) {
      message.info("크리에이터를 선택해주세요.");
      return;
    }
    const success = await cancelCampaignByAgency({
      campaign_id: selectedCampaign?.id,
      user_ids: selectedRowKeys,
    });
    if (success) {
      setSelectedRowKeys([]);
    }
  };

  const { data: campaignsData } = useCampaignListByAgency({
    page: currentPage,
    status: currentStatus,
    enabled: !!userInfo?.type_info,
  });

  const isCampaignListError = campaignsData === null;
  const campaigns = Array.isArray(campaignsData?.result)
    ? campaignsData.result
    : [];
  const total = campaignsData?.total ?? 0;

  const handleCampaignClick = async (campaign, shouldUpdateUrl = true) => {
    if (shouldUpdateUrl) {
      history.replace(`/campaign/manage?campaign_id=${campaign.id}`);
    }

    setSelectedCampaign(campaign);
    setIsDetailDrawerVisible(true);
  };

  // URL 파라미터 처리
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const campaignId = urlParams.get("campaign_id");

    if (campaignId && campaigns.length > 0 && !isDetailDrawerVisible) {
      const campaign = campaigns.find(
        (c) => String(c.id) === String(campaignId)
      );
      if (campaign) {
        handleCampaignClick(campaign, false);
      }
    }
  }, [location.search, campaigns]);

  const { data: paymentHistoryData } = useAgencyPaymentHistory({
    campaignId: selectedCampaign?.id,
    status: null,
    enabled: !!selectedCampaign?.id,
  });

  const handleOpenPaymentDetail = (item) => {
    if (!item?.pay_report_id) return;

    history.push({
      pathname: `/payments/agency-payments-history/${item.pay_report_id}`,
      state: item,
    });
  };

  return (
    <div>
      <h2 className="md:text-4xl text-3xl font-bold">캠페인 관리</h2>

      <Tabs
        defaultActiveKey="all"
        onChange={(key) => {
          const statusMap = {
            0: "pending",
            1: "accepted",
            2: "rejected",
            3: "invited",
          };
          setCurrentStatus(key === "all" ? null : statusMap[key]);
          setCurrentPage(1);
        }}
        className="font-normal mb-5"
        items={[
          { key: "all", label: "전체" },
          { key: "3", label: "초대" },
          { key: "0", label: "검토 중" },
          { key: "1", label: "승인" },
          { key: "2", label: "반려" },
        ]}
      />

      {isCampaignListError ? (
        <div className="py-12">
          <Empty
            description="캠페인 목록을 불러오지 못했습니다. 새로고침 후 다시 시도해주세요."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button size="small" onClick={() => window.location.reload()}>
              새로고침
            </Button>
          </Empty>
        </div>
      ) : (
        <div className="grid grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5 gap-[15px] md:gap-[25px]">
          {campaigns.map((campaign) => (
            <Router.CampaignCard
              key={campaign.id}
              campaign={campaign}
              variant="agency-manage"
              selected={selectedCampaign?.id === campaign.id}
              onClick={async () => {
                if (selectedCampaign?.id !== campaign.id) {
                  setShowAppliedList(false);
                  form.resetFields();
                  handleCampaignClick(campaign, true);
                } else {
                  setIsDetailDrawerVisible(true);
                }
              }}
            />
          ))}
        </div>
      )}

      <Drawer
        title={showAppliedList ? "신청 크리에이터 목록" : "캠페인 상세보기"}
        placement="right"
        onClose={(e) => {
          e?.stopPropagation();
          setIsDetailDrawerVisible(false);
          setSelectedCampaign(null);
          setShowAppliedList(false);
          form.resetFields();
          history.replace("/campaign/manage");
        }}
        onClick={(e) => e.stopPropagation()} // 드로워 전체에 이벤트 전파 중단 추가
        open={isDetailDrawerVisible}
        mask={false}
        maskClosable={true}
        width={showAppliedList ? 950 : 600}
        footer={null}
        extra={
          <div className="flex gap-2">
            <Button
              type="primary"
              size="small"
              className="md:!h-[32px] md:!px-[15px] md:!py-[4px] md:!text-2xl"
              style={{
                backgroundColor: "#171717",
              }}
              onClick={() => setShowAppliedList(!showAppliedList)}
            >
              {showAppliedList ? "캠페인 상세 정보" : "신청자 목록"}
            </Button>
          </div>
        }
      >
        {/* 캠페인 상세보기 */}
        {isDetailDrawerVisible && selectedCampaign ? (
          <div>
            {!showAppliedList ? (
              <>
                <Router.CampaignDetailInfo
                  selectedCampaign={selectedCampaign}
                  brandRecentVideos={selectedCampaign?.recent_tiktok_videos}
                />
              </>
            ) : (
              // 신청한 크리에이터 목록
              <>
                <Tabs
                  defaultActiveKey="all"
                  onChange={(key) => {
                    setInfluencerListStatus(key === "all" ? null : key);
                  }}
                  className="mb-5 font-normal"
                  items={[
                    { key: "all", label: "전체" },
                    { key: "invited", label: "초대" },
                    { key: "pending", label: "검토 중" },
                    { key: "accepted", label: "수락" },
                    { key: "rejected", label: "거절" },
                    { key: "cancelled", label: "취소" },
                  ]}
                />

                {(influencerListStatus === null ||
                  influencerListStatus === "pending") && (
                  <div className="mb-4 flex justify-end">
                    <Button onClick={handleCancelCampaign}>신청 취소</Button>
                  </div>
                )}

                <Table
                  dataSource={appliedData?.result ?? []}
                  onRow={() => ({
                    className: "cursor-default",
                  })}
                  rowKey={(record) => {
                    return record.user_id;
                  }}
                  rowClassName={(record) => {
                    const userId = record.user_id;
                    const isSelected = selectedRowKeys.includes(userId);
                    return isSelected ? "custom-selected-row" : "";
                  }}
                  scroll={{ x: "max-content" }}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (selectedKeys) => {
                      setSelectedRowKeys(selectedKeys);
                    },

                    getCheckboxProps: (record) => ({
                      disabled: record.status !== "pending",
                    }),
                  }}
                  className="custom-table"
                  columns={[
                    {
                      title: "닉네임",
                      dataIndex: "nickname",
                      key: "nickname",
                      width: 100,
                      align: "center",
                      render: (text) => (
                        <div className="break-all whitespace-pre-wrap">
                          {text || "닉네임 없음"}
                        </div>
                      ),
                    },
                    {
                      title: "성별",
                      dataIndex: "gender",
                      key: "gender",
                      width: 70,
                      align: "center",
                      render: (gender) => (gender === "M" ? "남성" : "여성"),
                    },
                    {
                      title: "신청일",
                      dataIndex: "created_at",
                      key: "created_at",
                      width: 100,
                      align: "center",
                      render: (date) => dayjs(date).format("YYYY-MM-DD HH:mm"),
                    },
                    {
                      title: "첨부파일",
                      dataIndex: "attachment_url",
                      key: "attachment_url",
                      width: 90,
                      align: "center",
                      render: (url) =>
                        url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-center"
                          >
                            파일
                          </a>
                        ) : (
                          "-"
                        ),
                    },
                    {
                      title: "메시지",
                      key: "message",
                      width: 70,
                      align: "center",
                      render: (_, record) =>
                        record.message ? (
                          <Popover
                            content={
                              <div className="max-w-[200px] whitespace-pre-wrap">
                                {record.message}
                              </div>
                            }
                            title="메시지"
                            trigger="click"
                          >
                            <Button size="small">보기</Button>
                          </Popover>
                        ) : (
                          "없음"
                        ),
                    },
                    {
                      title: "상태",
                      dataIndex: "status",
                      key: "status",
                      width: 70,
                      align: "center",
                      render: (status) => {
                        const statusInfo =
                          AGENCY_STATUS[status] ?? AGENCY_STATUS.default;
                        return (
                          <Tag
                            className={`border-none ${statusInfo.className} m-0`}
                          >
                            {statusInfo.label}
                          </Tag>
                        );
                      },
                    },
                  ]}
                  pagination={false}
                />
                {paymentHistoryData?.result?.length > 0 ? (
                  <section className="pt-5">
                    <div className="mt-8 mb-3 space-y-1">
                      <h4 className="text-[15px] font-bold text-[#111827] mb-2">
                        크리에이터별 정산
                      </h4>
                      <div className="space-y-0">
                        <p className="text-[12px] leading-relaxed text-[#64748b]">
                          서핑크리에서 정산이 완료된 크리에이터 목록입니다.
                        </p>
                        <p className="text-[12px] leading-relaxed text-[#64748b]">
                          에이전시 소속 크리에이터의 경우, 에이전시의 통장으로
                          정산되므로 크리에이터에게 따로 지급해야 합니다. 항목을
                          누르면 정산 상세 내역으로 이동합니다.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2.5">
                      {paymentHistoryData.result.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleOpenPaymentDetail(item)}
                          className="flex w-full items-center justify-between gap-4 rounded-xl border border-[#e7eaee] bg-[#fafbfc] px-4 py-3 text-left transition-colors"
                        >
                          <div className="flex flex-col">
                            <div
                              className={`inline-flex shrink-0 rounded-[8px] px-2 py-0.5 text-[11px] font-bold ${influencerPaymentStatusClass(
                                item.status
                              )}`}
                            >
                              {influencerPaymentStatusLabel(item.status)}
                            </div>
                            <div className="min-w-0 flex-1 flex flex-col">
                              <span className="truncate text-[14px] font-semibold text-[#111827] px-1 py-0.5">
                                {item.nickname}
                              </span>
                              {item.paid_at ? (
                                <div className="mt-1 text-[11px] text-[#94a3b8]">
                                  지급일 {item.paid_at}
                                </div>
                              ) : null}
                            </div>
                          </div>

                          <div className="shrink-0 text-right">
                            <div className="text-[11px] font-medium text-[#94a3b8]">
                              실지급액
                            </div>
                            <div className="mt-1 text-[15px] font-bold tabular-nums text-[#111827]">
                              {formatCurrency(
                                parseApiAmount(item.net_amount),
                                item.currency ?? "KRW"
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </section>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </Drawer>
      <Pagination
        current={currentPage}
        total={total}
        pageSize={pageSize}
        onChange={(page) => setCurrentPage(page)}
        showSizeChanger={false}
        className="mt-12 justify-center"
      />
    </div>
  );
};
